import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../../lib/utils';

export interface TaskyTabItem<T extends string = string> {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  /** 指定するとタブを react-router の Link として描画する（中クリック・新しいタブで開くを保つ）。
   *  全項目に指定したときだけ有効。混在すると button 扱いになる。 */
  href?: string;
  /** 指定すると右にカウントバッジを出す。0 のときは非表示だが幅は確保する（切替で幅が変わらないため） */
  count?: number;
  disabled?: boolean;
}

export interface TaskyTabsProps<T extends string = string> {
  items: TaskyTabItem<T>[];
  value: T;
  /** href 指定のリンクタブでは省略可。リンクでは修飾キー無しの左クリックのときだけ呼ばれる。 */
  onChange?: (value: T) => void;
  /** 既定 'underline' */
  variant?: 'underline' | 'pill';
  /** 既定 'md' */
  size?: 'md' | 'sm';
  /** タブ行の右端スロット（underline のみ） */
  trailing?: React.ReactNode;
  className?: string;
  'aria-label'?: string;
}

/** href 有無の混在警告は 1 回だけ出す。 */
let warnedMixedHref = false;

/** DS 正本 `#tabs` のカウントバッジ。Segmented と共通の見た目。 */
const countBadgeClass =
  'ml-1.5 inline-flex min-w-[20px] justify-center rounded-full bg-destructive px-1.5 text-[11px] font-bold leading-5 text-white';

const rowClass = {
  underline: { md: 'flex gap-6 border-b border-gray-200', sm: 'flex gap-4 border-b border-gray-200' },
  pill: { md: 'flex gap-2 flex-wrap', sm: 'flex gap-2 flex-wrap' },
} as const;

const tabClass = {
  underline: {
    md: 'text-sm font-medium pb-2 border-b-2 border-transparent text-gray-500 -mb-px transition-colors',
    sm: 'text-xs font-medium pb-1.5 border-b-2 border-transparent text-gray-500 -mb-px transition-colors',
  },
  pill: {
    md: 'px-4 py-2 rounded-full text-sm font-medium transition-colors',
    sm: 'px-3 py-1 rounded-full text-xs font-medium transition-colors',
  },
} as const;

const activeTabClass = {
  underline: 'border-primary text-primary',
  pill: 'bg-primary text-white',
} as const;

const inactiveTabClass = {
  underline: 'hover:text-dark',
  pill: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
} as const;

export function TaskyTabs<T extends string = string>({
  items,
  value,
  onChange,
  variant = 'underline',
  size = 'md',
  trailing,
  className,
  'aria-label': ariaLabel,
}: TaskyTabsProps<T>): JSX.Element {
  const buttonRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  const hrefCount = items.filter((item) => item.href !== undefined).length;
  const asLinks = items.length > 0 && hrefCount === items.length;
  const hasMixedHref = hrefCount > 0 && hrefCount < items.length;

  React.useEffect(() => {
    if (!hasMixedHref || process.env.NODE_ENV === 'production' || warnedMixedHref) return;
    warnedMixedHref = true;
    console.warn(
      '[TaskyTabs] href のある項目と無い項目が混在しています。すべて button として描画します'
    );
  }, [hasMixedHref]);

  const moveSelection = (from: number, direction: 1 | -1) => {
    const total = items.length;
    if (total === 0) return;
    let index = from < 0 ? (direction === 1 ? -1 : 0) : from;
    for (let step = 0; step < total; step += 1) {
      index = (index + direction + total) % total;
      const candidate = items[index];
      if (candidate && !candidate.disabled) {
        onChange?.(candidate.value);
        buttonRefs.current[index]?.focus();
        return;
      }
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveSelection(index, 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveSelection(index, -1);
    }
  };

  const itemClassName = (selected: boolean, disabled?: boolean) =>
    cn(
      'inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
      tabClass[variant][size],
      selected ? activeTabClass[variant] : inactiveTabClass[variant],
      disabled && 'opacity-50 cursor-not-allowed'
    );

  const itemContent = (item: TaskyTabItem<T>) => (
    <>
      {item.icon && <span className="mr-1.5 inline-flex items-center">{item.icon}</span>}
      {item.label}
      {item.count !== undefined && (
        <span
          data-testid={`tasky-tab-count-${item.value}`}
          className={cn(countBadgeClass, item.count === 0 && 'invisible')}
        >
          {item.count}
        </span>
      )}
    </>
  );

  /** 遷移は Link に任せる（preventDefault しない）。修飾キー付き・中クリックでは onChange を呼ばない。 */
  const handleLinkClick = (item: TaskyTabItem<T>) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    onChange?.(item.value);
  };

  const rowClassName = cn(
    rowClass[variant][size],
    trailing && variant === 'underline' && 'flex-1'
  );

  const links = items.map((item) => {
    const selected = item.value === value;
    if (item.disabled) {
      return (
        <span key={item.value} aria-disabled="true" className={itemClassName(selected, true)}>
          {itemContent(item)}
        </span>
      );
    }
    return (
      <Link
        key={item.value}
        to={item.href as string}
        aria-current={selected ? 'page' : undefined}
        className={itemClassName(selected)}
        onClick={handleLinkClick(item)}
      >
        {itemContent(item)}
      </Link>
    );
  });

  // 全項目が href を持つときはリンクの集合なので、タブ ARIA（tablist / ←→キー）を付けない。
  const row = asLinks ? (
    <nav aria-label={ariaLabel} className={rowClassName}>
      {links}
    </nav>
  ) : (
    <div role="tablist" aria-label={ariaLabel} className={rowClassName}>
      {items.map((item, index) => {
        const selected = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={selected}
            disabled={item.disabled}
            tabIndex={selected ? 0 : -1}
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            onClick={() => !item.disabled && onChange?.(item.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={itemClassName(selected, item.disabled)}
          >
            {itemContent(item)}
          </button>
        );
      })}
    </div>
  );

  if (trailing && variant === 'underline') {
    return (
      <div className={cn('flex items-end gap-4', className)}>
        {row}
        <div className="shrink-0 pb-1">{trailing}</div>
      </div>
    );
  }

  return <div className={className}>{row}</div>;
}
